import {
  ResponsesSubscriptionMovementsResponse,
  ResponsesSubscriptionVoluntaryEventsResponse,
  ResponsesSubscriptionVoluntaryEventSubscribersResponse,
} from '@genial/services/dist/models';
import { Utils } from '@genial/utils';

import {
  SubscriptionCustomerItemProps,
  SubscriptionHistoryItemProps,
  SubscriptionItemProps,
} from './types';

export const normalizeTimelineItem = (
  label: string,
  date?: Date | null
): SubscriptionItemProps['timelineItems'] => {
  return date
    ? [
        {
          label,
          formattedDate: Utils.Mask.DateFormat(date as any, 'dd/MM/yyyy'),
          date: new Date(date),
        },
      ]
    : [];
};

export const normalizeSubscriptionList = (
  data: ResponsesSubscriptionVoluntaryEventsResponse
): SubscriptionItemProps[] => {
  const ongoingSubscriptions =
    data.currentVoluntaryEvents?.map(
      (item) =>
        ({
          productDisplay: `${item.companyName} ${item.baseTradingCode}`,
          isinCode: item.baseIsinCode,
          executionDateFormatted: Utils.Mask.DateFormat(item.exDate as any, 'dd/MM/yyyy'),
          initialSolicitationDateFormatted: Utils.Mask.DateFormat(
            item.requestStartDate as any,
            'dd/MM/yyyy'
          ),
          limitDateFormatted: Utils.Mask.DateFormat(item.requestEndDate as any, 'dd/MM/yyyy'),
          settlementDateFormatted: Utils.Mask.DateFormat(item.settlementDate as any, 'dd/MM/yyyy'),
          daysSinceDeadline: item.requestStartDate
            ? Utils.Date.DaysDifferenceFromToday(new Date(item.requestStartDate))
            : 0,
          daysToSettlement: item.requestEndDate
            ? Utils.Date.DaysDifferenceFromToday(new Date(item.requestEndDate))
            : 0,
          customersEligible: item.customersWithoutRequest,
          unitaryPrice: item.stockPrice,
          unitaryPriceFormatted: Utils.Mask.Money(item.stockPrice),
          eventType: item.eventType,
          eventId: item.eventId,
          negotiationBaseCode: item.baseTradingCode,
          companyName: item.companyName,
          baseIsinCode: item.baseIsinCode,
          destinationIsinCode: item.destinationIsinCode || '--',
          timeLimitType: item.requestEndDate
            ? new Date(item.requestEndDate) < new Date()
              ? 'expired'
              : 'onGoing'
            : 'withoutDeadline',
          hasLeftovers: item.eventTypeDescription?.toLowerCase().includes('sobras') || false,
          timelineItems: [
            ...normalizeTimelineItem('Data Execução', item.exDate),
            ...normalizeTimelineItem('Data de inicio para solicitação', item.requestStartDate),
            ...normalizeTimelineItem(
              'Data e hora limite para solicitação e cancelamento',
              item.requestEndDate
            ),
            ...normalizeTimelineItem('Data de liquidação', item.settlementDate),
          ],
        } as SubscriptionItemProps)
    ) || [];

  const historicalSubscriptions =
    data.voluntaryEventsHistorical?.map(
      (item) =>
        ({
          productDisplay: item.companyName,
          isinCode: item.baseIsinCode,
          executionDateFormatted: Utils.Mask.DateFormat(
            item.dividendOrExTradingStartDate as any,
            'dd/MM/yyyy'
          ),
          initialSolicitationDateFormatted: Utils.Mask.DateFormat(
            item.exerciseStartDate as any,
            'dd/MM/yyyy'
          ),
          limitDateFormatted: Utils.Mask.DateFormat(item.exerciseEndDate as any, 'dd/MM/yyyy'),
          settlementDateFormatted: Utils.Mask.DateFormat(item.settlementDate as any, 'dd/MM/yyyy'),
          daysSinceDeadline: item.exerciseStartDate
            ? Utils.Date.DaysDifferenceFromToday(new Date(item.exerciseStartDate))
            : 0,
          daysToSettlement: item.settlementDate
            ? Utils.Date.DaysDifferenceFromToday(new Date(item.settlementDate))
            : 0,
          customersEligible: 0,
          unitaryPrice: 0,
          unitaryPriceFormatted: '--',
          eventType: item.eventType,
          eventId: item.eventId,
          negotiationBaseCode: item.baseTradingCode,
          companyName: item.companyName,
          baseIsinCode: item.baseIsinCode,
          destinationIsinCode: item.destinationTradingCode || '--',
          timeLimitType: 'expired',
          hasLeftovers: item.eventTypeDescription?.toLowerCase().includes('sobras') || false,
          timelineItems: [
            ...normalizeTimelineItem('Data Execução', item.dividendOrExTradingStartDate),
            ...normalizeTimelineItem('Data de inicio para solicitação', item.exerciseStartDate),
            ...normalizeTimelineItem(
              'Data e hora limite para solicitação e cancelamento',
              item.exerciseEndDate
            ),
            ...normalizeTimelineItem('Data de liquidação', item.settlementDate),
          ],
        } as SubscriptionItemProps)
    ) || [];

  return [...ongoingSubscriptions, ...historicalSubscriptions];
};

export const normalizeSubscriptionCustomersList = (
  data: ResponsesSubscriptionVoluntaryEventSubscribersResponse
): SubscriptionCustomerItemProps[] => {
  if (!data || !data.voluntaryEventSubscribers) {
    return [];
  }

  return data.voluntaryEventSubscribers.map((item) => ({
    customerNameNormalized: item.customerName || '--',
    advisorNameNormalized: item.advisorName || '--',
    phoneNumberNormalized: item.phoneNumber || '--',
    emailNormalized: item.email || '--',
    availableQuantity: item.availableQuantity,
    pnpAccountId: item.pnpAccountId || 0,
  }));
};

export const normalizeSubscriptionHistoryList = (
  data: ResponsesSubscriptionMovementsResponse
): SubscriptionHistoryItemProps[] => {
  if (!data || !data.movements) {
    return [];
  }

  return data.movements.map((item) => ({
    customerNameNormalized: item.customerName || '--',
    advisorNameNormalized: item.advisorName || '--',
    requestedQuantity: item.requestedQuantity || 0,
    totalValueFormatted: item.stockPrice ? Utils.Mask.Money(item.stockPrice) : '--',
    subscriptionRequestId: item.eventRequestId || 0,
    status: {
      ...item.status,
      isActive: true,
      backgroundColor: item.status?.iconBackgroundColor,
    },
    statusAlias: item.status?.status || '',
    ableToCancel: !item.requestStatus || item.requestStatus.toLowerCase().includes('solicitado'),
  }));
};
